/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxActionSheet from 'devextreme/ui/action_sheet';


import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';
import { IterableDifferHelper } from '../core/iterable-differ-helper';

import { DxiItemModule } from './nested/item-dxi';

import { DxiItemComponent } from './nested/item-dxi';




/**
 * The ActionSheet widget is a sheet containing a set of buttons located one under the other. These buttons usually represent several choices relating to a single task.
 */

export class DxActionSheetComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxActionSheet;


    /**
     * The text displayed in the button that closes the action sheet.
     */
    
    get cancelText(): string {
        return this._getOption('cancelText');
    }
    set cancelText(value: string) {
        this._setOption('cancelText', value);
    }


    /**
     * A data source used to fetch data to be displayed by the widget.
     */
    
    get dataSource(): DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate> {
        return this._getOption('dataSource');
    }
    set dataSource(value: DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate>) {
        this._setOption('dataSource', value);
    }


    /**
     * Specifies whether the widget responds to user interaction.
     */
    
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies the widget's height.
     */
    
    get height(): number|
        Function|
        string {
        return this._getOption('height');
    }
    set height(value: number|
        Function|
        string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * The time period in milliseconds before the onItemHold event is raised.
     */
    
    get itemHoldTimeout(): number {
        return this._getOption('itemHoldTimeout');
    }
    set itemHoldTimeout(value: number) {
        this._setOption('itemHoldTimeout', value);
    }


    /**
     * An array of items displayed by the widget.
     */
    
    get items(): Array<string|
        any|
        { disabled?: boolean, icon?: string, onClick?: Function|
        string, template?: any, text?: string, type?: string }> {
        return this._getOption('items');
    }
    set items(value: Array<string|
        any|
        { disabled?: boolean, icon?: string, onClick?: Function|
        string, template?: any, text?: string, type?: string }>) {
        this._setOption('items', value);
    }


    /**
     * Specifies a custom template for an item.
     */
    
    get itemTemplate(): any {
        return this._getOption('itemTemplate');
    }
    set itemTemplate(value: any) {
        this._setOption('itemTemplate', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Specifies whether or not to display the Cancel button in action sheet.
     */
    
    get showCancelButton(): boolean {
        return this._getOption('showCancelButton');
    }
    set showCancelButton(value: boolean) {
        this._setOption('showCancelButton', value);
    }


    /**
     * A Boolean value specifying whether or not the title of the action sheet is visible.
     */
    
    get showTitle(): boolean {
        return this._getOption('showTitle');
    }
    set showTitle(value: boolean) {
        this._setOption('showTitle', value);
    }


    /**
     * Specifies the element the action sheet popover points at.
     */
    
    get target(): Element|
        JQuery {
        return this._getOption('target');
    }
    set target(value: Element|
        JQuery) {
        this._setOption('target', value);
    }


    /**
     * The title of the action sheet.
     */
    
    get title(): string {
        return this._getOption('title');
    }
    set title(value: string) {
        this._setOption('title', value);
    }


    /**
     * Specifies whether or not to show the action sheet within a Popover widget.
     */
    
    get usePopover(): boolean {
        return this._getOption('usePopover');
    }
    set usePopover(value: boolean) {
        this._setOption('usePopover', value);
    }


    /**
     * A Boolean value specifying whether or not the ActionSheet widget is visible.
     */
    
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    
    get width(): number|
        Function|
        string {
        return this._getOption('width');
    }
    set width(value: number|
        Function|
        string) {
        this._setOption('width', value);
    }

    /**
     * A handler for the cancelClick event.
     */
     onCancelClick: EventEmitter<any>;

    /**
     * A handler for the contentReady event. Executed when the widget's content is ready. This handler may be executed multiple times during the widget's lifetime depending on the number of times its content changes.
     */
     onContentReady: EventEmitter<any>;

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the itemClick event.
     */
     onItemClick: EventEmitter<any>;

    /**
     * A handler for the itemContextMenu event.
     */
     onItemContextMenu: EventEmitter<any>;

    /**
     * A handler for the itemHold event.
     */
     onItemHold: EventEmitter<any>;

    /**
     * A handler for the itemRendered event.
     */
     onItemRendered: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the cancelTextChange event.
     */
     cancelTextChange: EventEmitter<string>;

    /**
     * A handler for the dataSourceChange event.
     */
     dataSourceChange: EventEmitter<DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate>>;

    /**
     * A handler for the disabledChange event.
     */
     disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the heightChange event.
     */
     heightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the hintChange event.
     */
     hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
     hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the itemHoldTimeoutChange event.
     */
     itemHoldTimeoutChange: EventEmitter<number>;

    /**
     * A handler for the itemsChange event.
     */
     itemsChange: EventEmitter<Array<string|
        any|
        { disabled?: boolean, icon?: string, onClick?: Function|
        string, template?: any, text?: string, type?: string }>>;

    /**
     * A handler for the itemTemplateChange event.
     */
     itemTemplateChange: EventEmitter<any>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the showCancelButtonChange event.
     */
     showCancelButtonChange: EventEmitter<boolean>;

    /**
     * A handler for the showTitleChange event.
     */
     showTitleChange: EventEmitter<boolean>;

    /**
     * A handler for the targetChange event.
     */
     targetChange: EventEmitter<Element|
        JQuery>;

    /**
     * A handler for the titleChange event.
     */
     titleChange: EventEmitter<string>;

    /**
     * A handler for the usePopoverChange event.
     */
     usePopoverChange: EventEmitter<boolean>;

    /**
     * A handler for the visibleChange event.
     */
     visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
     widthChange: EventEmitter<number|
        Function|
        string>;




    
    get itemsChildren(): QueryList<DxiItemComponent> {
        return this._getOption('items');
    }
    set itemsChildren(value) {
        this.setChildren('items', value);
    }


    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'cancelClick', emit: 'onCancelClick' },
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'itemClick', emit: 'onItemClick' },
            { subscribe: 'itemContextMenu', emit: 'onItemContextMenu' },
            { subscribe: 'itemHold', emit: 'onItemHold' },
            { subscribe: 'itemRendered', emit: 'onItemRendered' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { emit: 'cancelTextChange' },
            { emit: 'dataSourceChange' },
            { emit: 'disabledChange' },
            { emit: 'elementAttrChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'itemHoldTimeoutChange' },
            { emit: 'itemsChange' },
            { emit: 'itemTemplateChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'showCancelButtonChange' },
            { emit: 'showTitleChange' },
            { emit: 'targetChange' },
            { emit: 'titleChange' },
            { emit: 'usePopoverChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxActionSheet(element, options);
    }

    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('dataSource', changes);
        this.setupChanges('items', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('dataSource');
        this._idh.doCheck('items');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-action-sheet',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: IterableDifferHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'cancelText': [{ type: Input },],
'dataSource': [{ type: Input },],
'disabled': [{ type: Input },],
'elementAttr': [{ type: Input },],
'height': [{ type: Input },],
'hint': [{ type: Input },],
'hoverStateEnabled': [{ type: Input },],
'itemHoldTimeout': [{ type: Input },],
'items': [{ type: Input },],
'itemTemplate': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'showCancelButton': [{ type: Input },],
'showTitle': [{ type: Input },],
'target': [{ type: Input },],
'title': [{ type: Input },],
'usePopover': [{ type: Input },],
'visible': [{ type: Input },],
'width': [{ type: Input },],
'onCancelClick': [{ type: Output },],
'onContentReady': [{ type: Output },],
'onDisposing': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onItemClick': [{ type: Output },],
'onItemContextMenu': [{ type: Output },],
'onItemHold': [{ type: Output },],
'onItemRendered': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'cancelTextChange': [{ type: Output },],
'dataSourceChange': [{ type: Output },],
'disabledChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'heightChange': [{ type: Output },],
'hintChange': [{ type: Output },],
'hoverStateEnabledChange': [{ type: Output },],
'itemHoldTimeoutChange': [{ type: Output },],
'itemsChange': [{ type: Output },],
'itemTemplateChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'showCancelButtonChange': [{ type: Output },],
'showTitleChange': [{ type: Output },],
'targetChange': [{ type: Output },],
'titleChange': [{ type: Output },],
'usePopoverChange': [{ type: Output },],
'visibleChange': [{ type: Output },],
'widthChange': [{ type: Output },],
'itemsChildren': [{ type: ContentChildren, args: [DxiItemComponent, ] },],
};
}


export class DxActionSheetModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxiItemModule,
    DxTemplateModule
  ],
  declarations: [
    DxActionSheetComponent
  ],
  exports: [
    DxActionSheetComponent,
    DxiItemModule,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
